import React from 'react';
import { User, Calendar } from 'lucide-react';
import { StatusBadge, DomainCard } from '../ui';

// Map task domains → DomainCard domains
const TASK_DOMAIN_TO_CARD_DOMAIN = {
  quality: 'quality', staffing: 'staffing', access: 'access', finance: 'finance',
  compliance: 'practice', safety: 'practice', operations: 'ops', communications: 'practice',
};

const STATUS_VARIANT = {
  pending: 'info', in_progress: 'info', completed: 'on-track',
  skipped: 'watch', overdue: 'critical', delegated: 'info',
};
const STATUS_LABEL = {
  pending: 'Pending', in_progress: 'In Progress', completed: 'Completed',
  skipped: 'Skipped', overdue: 'Overdue', delegated: 'Delegated',
};
const DOMAIN_LABEL = {
  quality: 'Quality', staffing: 'Staffing', access: 'Access', finance: 'Finance',
  compliance: 'Compliance', safety: 'Safety', operations: 'Operations', communications: 'Communications',
};
const TIER_LABEL = { A: 'Tier A — Same-day', B: 'Tier B — 24h', C: 'Tier C — 72h', D: 'Tier D — Flexible' };
const TIER_COLOR = { A: 'text-red-critical', B: 'text-yellow-nn', C: 'text-text-secondary', D: 'text-text-muted' };
const PRIORITY_DOT_COLOR = { high: 'bg-red-critical', medium: 'bg-yellow-nn', low: 'bg-green-nn' };

/**
 * Task card — compact display for queue and library lists.
 *
 * Queue variant: shows status badge, due date, assigned owner.
 * Library variant: shows cadence, completion type, estimated time.
 *
 * All detail moves to the right panel — card is intentionally minimal.
 */
function TaskCard({ task, variant = 'queue', selected = false, onClick }) {
  const isQueue = variant === 'queue';
  const status = task.status || 'pending';
  const isOverdue = status === 'overdue' || (task.overdueHours && task.overdueHours > 0);
  const isOrphaned = task.isOrphaned;
  const alertLevel = isOrphaned ? 'critical' : isOverdue ? 'warning' : 'none';

  return (
    <DomainCard
      domain={TASK_DOMAIN_TO_CARD_DOMAIN[task.domain] || 'practice'}
      accentPlacement="left"
      density="compact"
      alertLevel={alertLevel}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick?.(e); }}
      className={[
        'flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-all',
        selected
          ? 'ring-1 ring-aqua shadow-sm'
          : 'hover:shadow-sm',
      ].join(' ')}
    >
      {/* Priority dot */}
      {task.priority && (
        <div className={`w-[7px] h-[7px] rounded-full shrink-0 ${PRIORITY_DOT_COLOR[task.priority] || 'bg-text-muted'}`} />
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Row 1: Name + Status (queue only) */}
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-body-sm font-semibold text-text-dark truncate">{task.taskName || task.name}</span>
          {isQueue && <StatusBadge variant={STATUS_VARIANT[status]}>{STATUS_LABEL[status]}</StatusBadge>}
        </div>

        {/* Row 2: Minimal metadata — due date + assigned owner (queue) or cadence + type (library) */}
        <div className="flex items-center gap-2 mt-0.5 text-caption text-text-muted truncate">
          {isQueue && task.dueDate && (
            <span className="flex items-center gap-0.5 shrink-0">
              <Calendar size={10} /> {task.dueDate}
            </span>
          )}
          {isQueue && (task.resolvedOwner || task.ownerRole) && (
            <span className="flex items-center gap-0.5 truncate">
              <User size={10} />
              <span className="truncate">{task.resolvedOwner?.name || task.ownerRole?.replace(/_/g, ' ')}</span>
            </span>
          )}
          {!isQueue && <span className="capitalize">{task.cadence}</span>}
          {!isQueue && task.completionType && <span>{task.completionType.replace(/_/g, ' + ')}</span>}
          {!isQueue && task.estimatedMinutes && <span>{task.estimatedMinutes}m</span>}
        </div>

        {/* Row 3: Workflow badge */}
        {task.workflowName && (
          <div className="text-caption text-aqua-dark mt-0.5 truncate">↳ {task.workflowName}</div>
        )}
      </div>
    </DomainCard>
  );
}

export { TaskCard, DOMAIN_LABEL, STATUS_LABEL, STATUS_VARIANT, TIER_LABEL, TIER_COLOR, PRIORITY_DOT_COLOR };
export default TaskCard;
